/* ==========================================================================
   Product Form Modal
   Task F23: Modal for adding/editing catalog products
   ========================================================================== */

import { useState, useEffect } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { Select, type SelectOption } from '@/components/ui/Select'
import { FormField } from '@/components/ui/FormField'
import { CategoryMultiSelect } from '@/components/admin/CategoryMultiSelect'
import { CustomFieldsEditor } from '@/components/admin/CustomFieldsEditor'
import { ProductNotesEditor } from '@/components/admin/ProductNotesEditor'
import { drugCodeLabel, isCanada } from '@/utils/countries'
import type {
  CatalogProductResponse,
  CatalogProductCreate,
  CatalogProductUpdate,
  CategoryBrief,
  CustomFieldDefinition,
  ProductNoteDefinition,
} from '@/api/types'

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

export interface ProductFormData {
  display_name: string
  product_code: string
  ndc: string
  ingredients: string
  strength: string
  dose_specific_directions: string
  default_additional_directions: string
  quantity: string
  unit_amount: string
  days_supply: string
  refills: string
  category_ids: string[]
  medical_necessity_statement: string
  custom_fields: CustomFieldDefinition[]
  product_notes: ProductNoteDefinition[]
}

export interface ProductFormModalProps {
  /** Whether the modal is open */
  open: boolean
  /** Callback when modal closes */
  onClose: () => void
  /** Callback when form is submitted successfully */
  onSubmit: (data: CatalogProductCreate | CatalogProductUpdate) => Promise<void>
  /** Product to edit (null for create mode) */
  product: CatalogProductResponse | null
  /**
   * Product to duplicate from. When set (and `product` is null), the form opens
   * in create mode pre-filled with this product's fields, except Product Name
   * and Product Code, which are left blank for the user to enter.
   */
  duplicateFrom?: CatalogProductResponse | null
  /** Loading state during submission */
  loading?: boolean
  /** The pharmacy's categories, selectable via checkboxes (PORTAL-77) */
  categories?: CategoryBrief[]
  /**
   * Creates a category inline and returns it (or null on failure).
   * The new category is auto-selected on this product.
   */
  onCreateCategory?: (name: string) => Promise<CategoryBrief | null>
  /**
   * The pharmacy's ISO-3166 country (PORTAL-165). Canadian pharmacies label the
   * drug code "DIN" and accept 8-11 digits; anything else keeps the NDC rules.
   */
  country?: string | null
}

/* --------------------------------------------------------------------------
   Refills Options
   -------------------------------------------------------------------------- */

const REFILLS_OPTIONS: SelectOption[] = Array.from({ length: 12 }, (_, i) => ({
  value: String(i),
  label: String(i),
}))

/* --------------------------------------------------------------------------
   Validation
   -------------------------------------------------------------------------- */

interface FormErrors {
  display_name?: string
  product_code?: string
  ndc?: string
  dose_specific_directions?: string
  quantity?: string
  unit_amount?: string
  days_supply?: string
  refills?: string
}

function validateForm(values: ProductFormData, country?: string | null): FormErrors {
  const errors: FormErrors = {}

  // Required fields
  if (!values.display_name.trim()) {
    errors.display_name = 'Product name is required'
  }

  if (!values.product_code.trim()) {
    errors.product_code = 'Product code is required'
  }

  // Dose specific directions: optional, but validate length if provided
  // (no required check — pharmacy can leave blank, prescriber fills in)

  // Quantity validation: optional, but validate format if provided
  if (values.quantity.trim()) {
    const qty = parseFloat(values.quantity)
    if (isNaN(qty) || qty <= 0) {
      errors.quantity = 'Quantity must be a positive number'
    }
  }

  if (!values.unit_amount.trim()) {
    errors.unit_amount = 'Unit/Amount is required'
  }

  // Days supply validation: optional, but validate range if provided
  if (values.days_supply.trim()) {
    const days = parseInt(values.days_supply, 10)
    if (isNaN(days) || days < 1 || days > 365) {
      errors.days_supply = 'Days supply must be between 1 and 365'
    }
  }

  // NDC validation (optional but must be valid format if provided)
  if (values.ndc.trim()) {
    if (isCanada(country)) {
      // DIN format (PORTAL-165): 8-11 bare digits
      if (!/^\d{8,11}$/.test(values.ndc.trim())) {
        errors.ndc = 'DIN must be 8-11 digits'
      }
    } else {
      // NDC format: 5-4-2 or 5-3-2 or 4-4-2 (with or without dashes)
      const ndcDigits = values.ndc.replace(/\D/g, '')
      if (ndcDigits.length < 10 || ndcDigits.length > 11) {
        errors.ndc = 'NDC must be 10-11 digits'
      }
    }
  }

  return errors
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function ProductFormModal({
  open,
  onClose,
  onSubmit,
  product,
  duplicateFrom = null,
  loading = false,
  categories = [],
  onCreateCategory,
  country,
}: ProductFormModalProps) {
  const isEditMode = product !== null

  // Form state
  const [formData, setFormData] = useState<ProductFormData>({
    display_name: '',
    product_code: '',
    ndc: '',
    ingredients: '',
    strength: '',
    dose_specific_directions: '',
    default_additional_directions: '',
    quantity: '',
    unit_amount: '',
    days_supply: '',
    refills: '0',
    category_ids: [],
    medical_necessity_statement: '',
    custom_fields: [],
    product_notes: [],
  })

  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  // Reset form when modal opens/closes or product changes
  useEffect(() => {
    if (open) {
      if (product) {
        // Edit mode - populate with product data
        setFormData({
          display_name: product.display_name,
          product_code: product.product_code,
          ndc: product.ndc || '',
          ingredients: product.ingredients || '',
          strength: product.strength || '',
          dose_specific_directions: product.dose_specific_directions || '',
          default_additional_directions: product.default_additional_directions || '',
          quantity: product.quantity || '',
          unit_amount: product.unit_amount,
          days_supply: product.days_supply != null ? String(product.days_supply) : '',
          refills: product.refills != null ? String(product.refills) : '',
          category_ids: product.categories.map((c) => c.id),
          medical_necessity_statement: product.medical_necessity_statement || '',
          custom_fields: product.custom_fields || [],
          product_notes: product.product_notes || [],
        })
      } else if (duplicateFrom) {
        // Duplicate mode - copy all fields except Product Name and Product Code
        setFormData({
          display_name: '',
          product_code: '',
          ndc: duplicateFrom.ndc || '',
          ingredients: duplicateFrom.ingredients || '',
          strength: duplicateFrom.strength || '',
          dose_specific_directions: duplicateFrom.dose_specific_directions || '',
          default_additional_directions: duplicateFrom.default_additional_directions || '',
          quantity: duplicateFrom.quantity || '',
          unit_amount: duplicateFrom.unit_amount,
          days_supply: duplicateFrom.days_supply != null ? String(duplicateFrom.days_supply) : '',
          refills: duplicateFrom.refills != null ? String(duplicateFrom.refills) : '',
          category_ids: duplicateFrom.categories.map((c) => c.id),
          medical_necessity_statement: duplicateFrom.medical_necessity_statement || '',
          custom_fields: duplicateFrom.custom_fields || [],
          product_notes: duplicateFrom.product_notes || [],
        })
      } else {
        // Create mode - reset to defaults
        setFormData({
          display_name: '',
          product_code: '',
          ndc: '',
          ingredients: '',
          strength: '',
          dose_specific_directions: '',
          default_additional_directions: '',
          quantity: '',
          unit_amount: '',
          days_supply: '',
          refills: '',
          category_ids: [],
          medical_necessity_statement: '',
          custom_fields: [],
          product_notes: [],
        })
      }
      setErrors({})
      setTouched({})
    }
  }, [open, product, duplicateFrom])

  // Handle field change
  function handleChange(field: keyof ProductFormData, value: string) {
    setFormData((prev) => ({ ...prev, [field]: value }))

    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  // Handle field blur
  function handleBlur(field: keyof ProductFormData) {
    setTouched((prev) => ({ ...prev, [field]: true }))

    // Validate single field
    const fieldErrors = validateForm(formData, country)
    if (fieldErrors[field as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [field]: fieldErrors[field as keyof FormErrors],
      }))
    }
  }

  // Handle form submission
  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()

    // Validate all fields
    const formErrors = validateForm(formData, country)
    setErrors(formErrors)
    setTouched({
      display_name: true,
      product_code: true,
      ndc: true,
      dose_specific_directions: true,
      quantity: true,
      unit_amount: true,
      days_supply: true,
      refills: true,
    })

    // Check for errors
    if (Object.keys(formErrors).length > 0) {
      return
    }

    // Build the data object
    const data: CatalogProductCreate | CatalogProductUpdate = {
      display_name: formData.display_name.trim(),
      product_code: formData.product_code.trim(),
      ndc: formData.ndc.trim() || null,
      ingredients: formData.ingredients.trim() || null,
      strength: formData.strength.trim() || null,
      dose_specific_directions: formData.dose_specific_directions.trim() || null,
      default_additional_directions: formData.default_additional_directions.trim() || null,
      quantity: formData.quantity.trim() || null,
      unit_amount: formData.unit_amount.trim(),
      days_supply: formData.days_supply.trim() ? parseInt(formData.days_supply, 10) : null,
      refills: formData.refills.trim() ? parseInt(formData.refills, 10) : null,
      category_ids: formData.category_ids,
      medical_necessity_statement: formData.medical_necessity_statement.trim() || null,
      custom_fields: formData.custom_fields.length > 0 ? formData.custom_fields : null,
      product_notes: formData.product_notes.length > 0 ? formData.product_notes : null,
    }

    await onSubmit(data)
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEditMode ? 'Edit Product' : duplicateFrom ? 'Duplicate Product' : 'Add Product'}
      size="xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Product Name */}
        <FormField
          label="Product Name"
          required
          error={touched.display_name ? errors.display_name : undefined}
          htmlFor="display_name"
        >
          <Input
            id="display_name"
            type="text"
            value={formData.display_name}
            onChange={(e) => handleChange('display_name', e.target.value)}
            onBlur={() => handleBlur('display_name')}
            error={touched.display_name && !!errors.display_name}
            disabled={loading}
            placeholder="e.g., Testosterone Cypionate 200mg/mL - 10mL vial"
          />
        </FormField>

        {/* Product Code and NDC */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            label="Product Code"
            required
            error={touched.product_code ? errors.product_code : undefined}
            htmlFor="product_code"
          >
            <Input
              id="product_code"
              type="text"
              value={formData.product_code}
              onChange={(e) => handleChange('product_code', e.target.value)}
              onBlur={() => handleBlur('product_code')}
              error={touched.product_code && !!errors.product_code}
              disabled={loading}
              placeholder="e.g., TC-200-10"
            />
          </FormField>

          <FormField
            label={drugCodeLabel(country)}
            helperText={isCanada(country) ? 'Optional - 8-11 digits' : 'Optional - 10-11 digits'}
            error={touched.ndc ? errors.ndc : undefined}
            htmlFor="ndc"
          >
            <Input
              id="ndc"
              type="text"
              value={formData.ndc}
              onChange={(e) => handleChange('ndc', e.target.value)}
              onBlur={() => handleBlur('ndc')}
              error={touched.ndc && !!errors.ndc}
              disabled={loading}
              placeholder={isCanada(country) ? 'e.g., 02240084' : 'e.g., 12345-678-90'}
            />
          </FormField>
        </div>

        {/* Categories */}
        <FormField
          label="Categories"
          htmlFor="product-categories"
          helperText="Optional - a product can belong to several categories. Type a new name to create one; rename or delete from the Categories page."
        >
          <CategoryMultiSelect
            categories={categories}
            selectedIds={formData.category_ids}
            onChange={(ids) =>
              setFormData((prev) => ({ ...prev, category_ids: ids }))
            }
            onCreateCategory={onCreateCategory}
            inputId="product-categories"
            disabled={loading}
          />
        </FormField>

        {/* Ingredients */}
        <FormField
          label="Ingredients"
          helperText="Optional - Active ingredients or formula details"
          htmlFor="ingredients"
        >
          <Textarea
            id="ingredients"
            value={formData.ingredients}
            onChange={(e) => handleChange('ingredients', e.target.value)}
            disabled={loading}
            rows={2}
            placeholder="e.g., Testosterone Cypionate 200mg/mL in grapeseed oil"
          />
        </FormField>

        {/* Strength */}
        <FormField
          label="Strength"
          helperText="Optional - Product strength"
          htmlFor="strength"
        >
          <Input
            id="strength"
            type="text"
            value={formData.strength}
            onChange={(e) => handleChange('strength', e.target.value)}
            disabled={loading}
            placeholder="e.g., 200mg/mL"
          />
        </FormField>

        {/* Dose Specific Directions */}
        <FormField
          label="Dose Specific Directions"
          error={touched.dose_specific_directions ? errors.dose_specific_directions : undefined}
          htmlFor="dose_specific_directions"
          helperText="Optional — prescriber will fill in if blank"
        >
          <Textarea
            id="dose_specific_directions"
            value={formData.dose_specific_directions}
            onChange={(e) => handleChange('dose_specific_directions', e.target.value)}
            onBlur={() => handleBlur('dose_specific_directions')}
            error={touched.dose_specific_directions && !!errors.dose_specific_directions}
            disabled={loading}
            rows={2}
            placeholder="e.g., Inject 0.5mL subcutaneously once weekly"
          />
        </FormField>

        {/* Default Additional Directions */}
        <FormField
          label="Default Additional Directions"
          helperText="Optional - Read-only for prescriber, appended to SIG on fax"
          htmlFor="default_additional_directions"
        >
          <Textarea
            id="default_additional_directions"
            value={formData.default_additional_directions}
            onChange={(e) => handleChange('default_additional_directions', e.target.value)}
            disabled={loading}
            rows={2}
            placeholder="e.g., Rotate injection sites. Store at room temperature."
          />
        </FormField>

        {/* Quantity, Unit/Amount, Days Supply, Refills */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <FormField
            label="Quantity"
            error={touched.quantity ? errors.quantity : undefined}
            htmlFor="quantity"
          >
            <Input
              id="quantity"
              type="text"
              inputMode="decimal"
              value={formData.quantity}
              onChange={(e) => handleChange('quantity', e.target.value)}
              onBlur={() => handleBlur('quantity')}
              error={touched.quantity && !!errors.quantity}
              disabled={loading}
              placeholder="10"
            />
          </FormField>

          <FormField
            label="Unit/Amount"
            required
            error={touched.unit_amount ? errors.unit_amount : undefined}
            htmlFor="unit_amount"
            helperText="e.g., mL, 60g, 15ml vial"
          >
            <Input
              id="unit_amount"
              type="text"
              value={formData.unit_amount}
              onChange={(e) => handleChange('unit_amount', e.target.value)}
              onBlur={() => handleBlur('unit_amount')}
              error={touched.unit_amount && !!errors.unit_amount}
              disabled={loading}
              placeholder="e.g., mL, 60g, 15ml vial"
            />
          </FormField>

          <FormField
            label="Days Supply"
            error={touched.days_supply ? errors.days_supply : undefined}
            htmlFor="days_supply"
          >
            <Input
              id="days_supply"
              type="text"
              inputMode="numeric"
              value={formData.days_supply}
              onChange={(e) => handleChange('days_supply', e.target.value)}
              onBlur={() => handleBlur('days_supply')}
              error={touched.days_supply && !!errors.days_supply}
              disabled={loading}
              placeholder="30"
            />
          </FormField>

          <FormField
            label="Refills"
            error={touched.refills ? errors.refills : undefined}
            htmlFor="refills"
          >
            <Select
              options={REFILLS_OPTIONS}
              value={formData.refills}
              onChange={(value) => handleChange('refills', value)}
              error={touched.refills && !!errors.refills}
              disabled={loading}
              placeholder="Select"
            />
          </FormField>
        </div>

        {/* Medical Necessity Statement */}
        <FormField
          label="Medical Necessity Statement"
          helperText="Optional — displayed read-only to prescribers when signing off on this product"
          htmlFor="medical_necessity_statement"
        >
          <Textarea
            id="medical_necessity_statement"
            value={formData.medical_necessity_statement}
            onChange={(e) => handleChange('medical_necessity_statement', e.target.value)}
            disabled={loading}
            rows={2}
            placeholder="e.g., Medically necessary for hormone replacement therapy"
          />
        </FormField>

        {/* Product Notes */}
        <div className="pt-2 border-t border-gray-200">
          <ProductNotesEditor
            value={formData.product_notes}
            onChange={(notes) =>
              setFormData((prev) => ({ ...prev, product_notes: notes }))
            }
            disabled={loading}
          />
        </div>

        {/* Custom Fields */}
        <div className="pt-2 border-t border-gray-200">
          <CustomFieldsEditor
            value={formData.custom_fields}
            onChange={(fields) =>
              setFormData((prev) => ({ ...prev, custom_fields: fields }))
            }
            disabled={loading}
          />
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
            className="sm:order-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            className="sm:order-2 sm:ml-auto"
          >
            {isEditMode ? 'Save Changes' : duplicateFrom ? 'Duplicate Product' : 'Add Product'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export default ProductFormModal
